import React, { useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import * as XLSX from 'xlsx'
import moment from 'moment'
import {
  CCard,
  CCardBody,
  CRow,
  CCol,
  CButton,
  CLoadingButton,
  CFormInput,
  CAlert,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
  CSpinner,
  CBadge,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import {
  cilArrowLeft,
  cilCheck,
  cilCheckAlt,
  cilCheckCircle,
  cilCloudDownload,
  cilCloudUpload,
  cilFile,
  cilInfo,
  cilWarning,
  cilX,
} from '@coreui/icons'
import ToastComponent from 'src/components/common/TaostComponent'
import RoundService from 'src/service/RoundService'
import SampleRoundCSV from '../../assets/SampleRoundFile.csv'

const WIZARD_STEPS = ['Upload File', 'Validate Rounds', 'Review & Import']
const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB

// Mirrors the date/time formats the backend's parseDate()/parseTime() accept, so a row that
// looks valid here won't get silently rejected on import. Every numeric format is day-first,
// matching the backend's 'd/m/Y' - a "3/10/2026" is 3 October, never 10 March.
const DATE_FORMATS = [
  'DD/MM/YYYY',
  'DD.MM.YYYY',
  'DD-MM-YYYY',
  'D MMM YYYY',
  'D MMMM YYYY',
  'YYYY-MM-DD',
]
const TIME_FORMATS = [
  'h:mm A',
  'h:mm a',
  'h:mmA',
  'h:mma',
  'h A',
  'h a',
  'hA',
  'ha',
  'HH:mm',
  'HH.mm',
]

const RESULT_META = {
  valid: { label: 'Valid', color: 'success', icon: cilCheckCircle, accent: '#198754' },
  missing_round: { label: 'Missing Round', color: 'danger', icon: cilWarning, accent: '#dc3545' },
  missing_start: {
    label: 'Missing Start Date/Time',
    color: 'danger',
    icon: cilWarning,
    accent: '#dc3545',
  },
  missing_end: {
    label: 'Missing End Date/Time',
    color: 'danger',
    icon: cilWarning,
    accent: '#dc3545',
  },
  invalid_start: {
    label: 'Invalid Start Date/Time',
    color: 'warning',
    icon: cilWarning,
    accent: '#ffc107',
  },
  invalid_end: {
    label: 'Invalid End Date/Time',
    color: 'warning',
    icon: cilWarning,
    accent: '#ffc107',
  },
  invalid_range: {
    label: 'End Before Start',
    color: 'warning',
    icon: cilWarning,
    accent: '#ffc107',
  },
  duplicate_round: {
    label: 'Duplicate Round #',
    color: 'info',
    icon: cilWarning,
    accent: '#0dcaf0',
  },
  overlapping_range: {
    label: 'Overlaps Another Row',
    color: 'info',
    icon: cilWarning,
    accent: '#0dcaf0',
  },
}

const tintStyle = (hex, alphaHex) => ({ backgroundColor: `${hex}${alphaHex}` })

const guessColumn = (headers, patterns) =>
  headers.find((h) =>
    patterns.some((p) =>
      String(h)
        .toLowerCase()
        .replace(/[^a-z]/g, '')
        .includes(p),
    ),
  ) || ''

// Zero-pads a purely numeric day-first date ("3/10/2026", "9/01/2027") to "03/10/2026" /
// "09/01/2027". moment's strict mode enforces an exact digit count per token, so DD/MM/YYYY
// alone rejects an unpadded day or month that the backend's Carbon::createFromFormat('d/m/Y')
// would happily accept. Separator and order are left untouched - this only pads.
const padNumericDate = (str) => {
  const parts = /^(\d{1,2})([/.-])(\d{1,2})\2(\d{4})$/.exec(str)
  if (!parts) return str
  return `${parts[1].padStart(2, '0')}${parts[2]}${parts[3].padStart(2, '0')}${parts[2]}${parts[4]}`
}

const parseDateFlexible = (raw) => {
  if (raw instanceof Date) return moment(raw)
  const str = padNumericDate(String(raw ?? '').trim())
  if (!str) return null
  for (const fmt of DATE_FORMATS) {
    const m = moment(str, fmt, true)
    if (m.isValid()) return m
  }
  return null
}

const parseTimeFlexible = (raw) => {
  if (raw instanceof Date) return moment(raw)
  const str = String(raw ?? '').trim()
  if (!str) return null
  for (const fmt of TIME_FORMATS) {
    const m = moment(str, fmt, true)
    if (m.isValid()) return m
  }
  return null
}

// Snaps a date/time to its canonical "YYYY-MM-DD" / "HH:mm" form (both of which the backend
// also accepts), so what's shown, edited and submitted always agree. Falls back to the raw
// text when it can't be parsed, so the user can still see what they need to fix.
const normalizeDateField = (raw) => {
  const m = parseDateFlexible(raw)
  if (m) return { text: m.format('YYYY-MM-DD'), moment: m, valid: true }
  return {
    text: raw instanceof Date ? moment(raw).format('YYYY-MM-DD') : String(raw ?? '').trim(),
    moment: null,
    valid: false,
  }
}

const normalizeTimeField = (raw) => {
  const m = parseTimeFlexible(raw)
  if (m) return { text: m.format('HH:mm'), moment: m, valid: true }
  return {
    text: raw instanceof Date ? moment(raw).format('HH:mm') : String(raw ?? '').trim(),
    moment: null,
    valid: false,
  }
}

const displayDate = (text) =>
  text && moment(text, 'YYYY-MM-DD', true).isValid()
    ? moment(text, 'YYYY-MM-DD').format('D.MM.YYYY')
    : text || '—'

const displayTime = (text) =>
  text && moment(text, 'HH:mm', true).isValid()
    ? moment(text, 'HH:mm').format('h:mm A')
    : text || '—'

const buildRow = (base) => {
  const round = String(base.round ?? '').trim()
  const sd = normalizeDateField(base.start_date)
  const st = normalizeTimeField(base.start_time)
  const ed = normalizeDateField(base.end_date)
  const et = normalizeTimeField(base.end_time)

  let result = null
  if (!round) result = 'missing_round'
  else if (!sd.text || !st.text) result = 'missing_start'
  else if (!ed.text || !et.text) result = 'missing_end'
  else if (!sd.valid || !st.valid) result = 'invalid_start'
  else if (!ed.valid || !et.valid) result = 'invalid_end'

  let startDT = null
  let endDT = null
  if (sd.valid && st.valid) {
    startDT = sd.moment
      .clone()
      .set({ hour: st.moment.hours(), minute: st.moment.minutes(), second: 0 })
  }
  if (ed.valid && et.valid) {
    endDT = ed.moment
      .clone()
      .set({ hour: et.moment.hours(), minute: et.moment.minutes(), second: 0 })
  }
  if (!result && startDT && endDT && !endDT.isAfter(startDT)) result = 'invalid_range'

  return {
    round,
    start_date: sd.text,
    start_time: st.text,
    end_date: ed.text,
    end_time: et.text,
    startDT,
    endDT,
    result,
  }
}

// Format/logic checks happen row-by-row first; duplicate round numbers and overlapping windows
// can only be judged against the *whole* file, so that's a second pass over the built rows -
// mirrors the backend's own within-upload duplicate/overlap checks (it also checks against
// rounds already in the DB, which this client-side pass can't see until import is attempted).
const buildValidatedRows = (baseRows) => {
  const built = baseRows.map(buildRow)
  const roundCounts = {}
  built.forEach((r) => {
    if (!r.result && r.round) roundCounts[r.round] = (roundCounts[r.round] || 0) + 1
  })

  return built.map((r, idx) => {
    let result = r.result
    if (!result) {
      if (roundCounts[r.round] > 1) {
        result = 'duplicate_round'
      } else if (
        r.startDT &&
        r.endDT &&
        built.some(
          (other, oidx) =>
            oidx !== idx &&
            !other.result &&
            other.startDT &&
            other.endDT &&
            !(r.endDT.isSameOrBefore(other.startDT) || r.startDT.isSameOrAfter(other.endDT)),
        )
      ) {
        result = 'overlapping_range'
      }
    }
    result = result || 'valid'
    return { row: idx + 1, ...r, result, included: result === 'valid' }
  })
}

const MiniStepper = ({ current, maxReached, onStepClick }) => (
  <div className="d-flex align-items-center mb-4 flex-wrap">
    {WIZARD_STEPS.map((label, idx) => {
      const n = idx + 1
      const done = n < current
      const active = n === current
      const clickable = n <= maxReached
      return (
        <React.Fragment key={label}>
          <div
            className="d-flex align-items-center gap-2"
            style={{ cursor: clickable ? 'pointer' : 'default', opacity: clickable ? 1 : 0.5 }}
            onClick={() => clickable && onStepClick(n)}
          >
            <div
              className={`d-flex align-items-center justify-content-center rounded-circle flex-shrink-0 ${
                done ? 'bg-success text-white' : active ? 'bg-primary text-white' : 'bg-body border'
              }`}
              style={{ width: 28, height: 28, fontSize: 13 }}
            >
              {done ? <CIcon icon={cilCheckAlt} size="sm" /> : n}
            </div>
            <span className={`small ${active ? 'fw-semibold text-primary' : ''}`}>{label}</span>
          </div>
          {n < WIZARD_STEPS.length && (
            <div
              className={`mx-2 ${done ? 'bg-success' : 'bg-secondary-subtle'}`}
              style={{ height: 2, width: 32 }}
            />
          )}
        </React.Fragment>
      )
    })}
  </div>
)

const InfoBox = ({ children, color = 'info' }) => (
  <CAlert color={color} className="d-flex align-items-start gap-2 border-0">
    <CIcon icon={cilInfo} className="flex-shrink-0 mt-1" />
    <div>{children}</div>
  </CAlert>
)

const TONE_HEX = {
  success: '#198754',
  danger: '#dc3545',
  warning: '#b45309',
  info: '#087990',
  secondary: '#495057',
}

const StatTile = ({ label, value, tone, icon }) => {
  const hex = TONE_HEX[tone] || TONE_HEX.secondary
  return (
    <div
      className="rounded-4 p-3 flex-fill d-flex align-items-center gap-3"
      style={{ ...tintStyle(hex, '14'), border: `1px solid ${hex}33` }}
    >
      <div
        className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
        style={{ width: 38, height: 38, backgroundColor: hex, color: '#fff' }}
      >
        <CIcon icon={icon} size="sm" />
      </div>
      <div>
        <div className="fs-4 fw-bold lh-1" style={{ color: hex }}>
          {value}
        </div>
        <div className="small text-medium-emphasis">{label}</div>
      </div>
    </div>
  )
}

// Round Import: upload a CSV/Excel file, validate each row's round number, start/end date and
// time against the accepted formats plus within-file duplicate/overlap checks, fix any of it
// inline, then commit only the rows left included. Mirrors the Player Import wizard's 3-step
// shape (Upload File -> Validate -> Review & Import), adapted for round fields.
const ImportRound = () => {
  const navigate = useNavigate()
  const fileInputRef = useRef(null)

  const [wizardStep, setWizardStep] = useState(1)
  const [maxWizardStep, setMaxWizardStep] = useState(1)

  const [fileName, setFileName] = useState('')
  const [fileRowCount, setFileRowCount] = useState(0)
  const [busy, setBusy] = useState(false)
  const [isDragging, setIsDragging] = useState(false)

  const [rows, setRows] = useState([]) // { row, round, start_date, start_time, end_date, end_time, result, included }
  const [editingRow, setEditingRow] = useState(null)
  const [editDraft, setEditDraft] = useState({
    round: '',
    start_date: '',
    start_time: '',
    end_date: '',
    end_time: '',
  })

  const [importing, setImporting] = useState(false)
  const [importErrors, setImportErrors] = useState([])

  const goToStep = (n) => {
    setWizardStep(n)
    setMaxWizardStep((m) => Math.max(m, n))
  }

  const resetAll = () => {
    setWizardStep(1)
    setMaxWizardStep(1)
    setFileName('')
    setFileRowCount(0)
    setRows([])
    setImportErrors([])
  }

  const handleFile = (file) => {
    if (!file) return
    if (file.size > MAX_FILE_SIZE) {
      ToastComponent('File is too large. Maximum size is 10MB.', 'error')
      return
    }
    setBusy(true)
    const reader = new FileReader()
    reader.onload = (evt) => {
      let json = []
      let rawHeaders = []
      try {
        const isCsv = /\.csv$/i.test(file.name)
        // CSV text has no cell types, so SheetJS guesses them - and it reads an ambiguous
        // "3/10/2026" as US M/D/YYYY, turning 3 October into 10 March. raw:true keeps every
        // CSV cell as the literal text so parseDateFlexible() applies DD/MM/YYYY first, the
        // same order the backend's parseDate() uses. Real spreadsheets are left with
        // cellDates:true: their date cells are unambiguous serials, not text.
        const workbook = XLSX.read(
          evt.target.result,
          isCsv ? { type: 'string', raw: true } : { type: 'array', cellDates: true },
        )
        const sheet = workbook.Sheets[workbook.SheetNames[0]]
        json = XLSX.utils.sheet_to_json(sheet, { defval: '' })
        rawHeaders = json.length ? Object.keys(json[0]) : []
      } catch (err) {
        ToastComponent('Could not read this file. Please check the format and try again.', 'error')
        setBusy(false)
        return
      }

      if (!rawHeaders.length) {
        ToastComponent('This file has no rows to import.', 'error')
        setBusy(false)
        return
      }

      const detected = {
        round: guessColumn(rawHeaders, ['round', 'rdnumber', 'roundno', 'rd']),
        start_date: guessColumn(rawHeaders, ['startdate', 'datestart', 'roundstart']),
        start_time: guessColumn(rawHeaders, ['starttime', 'lockoutstart', 'timestart']),
        end_date: guessColumn(rawHeaders, ['enddate', 'dateend', 'roundend']),
        end_time: guessColumn(rawHeaders, ['endtime', 'lockoutend', 'timeend']),
      }

      const baseRows = json.map((r) => ({
        round: detected.round ? r[detected.round] : '',
        start_date: detected.start_date ? r[detected.start_date] : '',
        start_time: detected.start_time ? r[detected.start_time] : '',
        end_date: detected.end_date ? r[detected.end_date] : '',
        end_time: detected.end_time ? r[detected.end_time] : '',
      }))

      setFileName(file.name)
      setFileRowCount(json.length)
      setRows(buildValidatedRows(baseRows))
      setBusy(false)
    }
    if (/\.csv$/i.test(file.name)) reader.readAsText(file)
    else reader.readAsArrayBuffer(file)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setIsDragging(false)
    handleFile(e.dataTransfer.files?.[0])
  }

  const toggleRowIncluded = (row) => {
    setRows((prev) =>
      prev.map((r) =>
        r.row === row && r.result === 'valid' ? { ...r, included: !r.included } : r,
      ),
    )
  }

  const startEdit = (r) => {
    setEditingRow(r.row)
    setEditDraft({
      round: r.round,
      start_date: r.start_date,
      start_time: r.start_time,
      end_date: r.end_date,
      end_time: r.end_time,
    })
  }

  const cancelEdit = () => {
    setEditingRow(null)
    setEditDraft({ round: '', start_date: '', start_time: '', end_date: '', end_time: '' })
  }

  const saveEdit = (row) => {
    const draft = {
      round: editDraft.round.trim(),
      start_date: editDraft.start_date.trim(),
      start_time: editDraft.start_time.trim(),
      end_date: editDraft.end_date.trim(),
      end_time: editDraft.end_time.trim(),
    }
    setRows((prev) => {
      const baseRows = prev.map((r) =>
        r.row === row
          ? draft
          : {
              round: r.round,
              start_date: r.start_date,
              start_time: r.start_time,
              end_date: r.end_date,
              end_time: r.end_time,
            },
      )
      const rebuilt = buildValidatedRows(baseRows)
      // Re-validating the whole file can flip other rows' duplicate/overlap status, but a
      // manually-skipped valid row shouldn't silently become "included" again just because
      // this edit was saved.
      return rebuilt.map((r, idx) => {
        if (r.row === row) return r
        const prevRow = prev[idx]
        if (r.result === 'valid' && prevRow?.result === 'valid')
          return { ...r, included: prevRow.included }
        return r
      })
    })
    cancelEdit()
  }

  const summary = useMemo(() => {
    const valid = rows.filter((r) => r.result === 'valid').length
    return { total: rows.length, valid, needsAttention: rows.length - valid }
  }, [rows])

  const readyRows = rows.filter((r) => r.included)

  const runImport = () => {
    if (!readyRows.length) return
    setImporting(true)
    setImportErrors([])
    const roundData = readyRows.map((r) => ({
      round: r.round,
      start_date: r.start_date,
      start_time: r.start_time,
      end_date: r.end_date,
      end_time: r.end_time,
    }))
    RoundService.importRound({ roundData })
      .then((res) => {
        if (res.success) {
          ToastComponent(
            res.message || `Successfully imported ${res.inserted ?? readyRows.length} round(s).`,
            'success',
          )
          resetAll()
          navigate('/rounds')
        } else {
          const errors = Array.isArray(res.errors)
            ? res.errors
            : res.errors
            ? Object.values(res.errors).flat()
            : ['Import failed. Please check your rows.']
          setImportErrors(errors)
          ToastComponent(res.message || 'Import failed. Please check the errors below.', 'error')
        }
      })
      .catch(() => ToastComponent('Import failed. Please try again.', 'error'))
      .finally(() => setImporting(false))
  }

  const renderUpload = () => (
    <>
      <CRow className="g-4">
        <CCol lg={5}>
          <CCard className="border-secondary-subtle rounded-4 h-100">
            <CCardBody>
              <div className="fw-semibold fs-6 mb-1">Download Template</div>
              <div className="small mb-3">Use the template to prepare your round records.</div>
              <a
                href={SampleRoundCSV}
                download="Round-Sample-CSV.csv"
                target="_blank"
                rel="noreferrer"
              >
                <CButton color="primary" variant="outline" className="mb-4">
                  <CIcon icon={cilCloudDownload} className="me-1" />
                  Download Round Template
                </CButton>
              </a>
              <div className="fw-semibold mb-2">Required columns</div>
              <ul className="ps-3 mb-3 small">
                <li>Round</li>
                <li>Start Date</li>
                <li>Start Time</li>
                <li>End Date</li>
                <li>End Time</li>
              </ul>
              <InfoBox color="light">
                Dates: <strong>DD/MM/YYYY</strong>, <strong>DD.MM.YYYY</strong>,{' '}
                <strong>YYYY-MM-DD</strong>, or &ldquo;1 Jan 2025&rdquo;. Times: 12-hour (e.g.{' '}
                <strong>9:00 AM</strong>) or 24-hour (e.g. <strong>09:00</strong>). Round numbers
                must be unique and each round&rsquo;s window must not overlap another round.
              </InfoBox>
            </CCardBody>
          </CCard>
        </CCol>

        <CCol lg={7}>
          <CCard className="border-secondary-subtle rounded-4 h-100">
            <CCardBody>
              <div className="fw-semibold fs-6 mb-1">Upload Completed Spreadsheet</div>
              <div className="small mb-3">
                Drag and drop an XLSX or CSV file, or browse your computer.
              </div>

              {!fileName ? (
                <div
                  className="border rounded-4 d-flex flex-column align-items-center justify-content-center text-center p-4"
                  style={{
                    cursor: 'pointer',
                    minHeight: 180,
                    backgroundColor: isDragging ? 'rgba(var(--cui-primary-rgb), 0.06)' : undefined,
                    borderStyle: 'dashed',
                  }}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => {
                    e.preventDefault()
                    setIsDragging(true)
                  }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={handleDrop}
                >
                  <CIcon icon={cilCloudUpload} size="xl" className="text-primary mb-2" />
                  <div>
                    Drag and drop an XLSX or CSV file, or{' '}
                    <span className="text-primary">browse</span>
                  </div>
                  <div className="text-medium-emphasis small mt-1">Maximum file size 10MB</div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    hidden
                    accept=".csv,.xlsx,.xls"
                    onChange={(e) => {
                      handleFile(e.target.files?.[0])
                      e.target.value = ''
                    }}
                  />
                </div>
              ) : (
                <CCard className="border-secondary-subtle rounded-3">
                  <CCardBody className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <div className="d-flex align-items-center gap-2">
                      <CIcon icon={cilFile} className="text-success" size="xl" />
                      <div>
                        <div className="fw-semibold">{fileName}</div>
                        <div className="small text-success d-flex align-items-center gap-1">
                          <CIcon icon={cilCheckCircle} size="sm" />
                          {fileRowCount} row{fileRowCount === 1 ? '' : 's'} detected
                        </div>
                      </div>
                    </div>
                    <CButton color="link" className="text-danger p-0" onClick={resetAll}>
                      Remove
                    </CButton>
                  </CCardBody>
                </CCard>
              )}

              {busy && (
                <div className="d-flex align-items-center gap-2 small text-medium-emphasis mt-2">
                  <CSpinner size="sm" />
                  Reading and validating your file…
                </div>
              )}

              <div className="text-medium-emphasis small mt-2">
                <a
                  href={SampleRoundCSV}
                  download="Round-Sample-CSV.csv"
                  target="_blank"
                  rel="noreferrer"
                  className="text-medium-emphasis text-decoration-none"
                >
                  <CIcon icon={cilCloudDownload} size="sm" className="me-1" />
                  Download Sample CSV
                </a>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      <div className="d-flex justify-content-between mt-4">
        <CButton color="secondary" variant="outline" onClick={() => navigate('/rounds')}>
          Cancel
        </CButton>
        <CButton color="primary" disabled={!rows.length} onClick={() => goToStep(2)}>
          Review File
        </CButton>
      </div>
    </>
  )

  const renderValidate = () => (
    <>
      <CCard className="border-secondary-subtle rounded-4 mb-3">
        <CCardBody className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div className="d-flex align-items-center gap-2">
            <CIcon icon={cilFile} className="text-primary" />
            <div>
              <div className="fw-semibold">{fileName}</div>
              <div className="small text-medium-emphasis">{fileRowCount} rows</div>
            </div>
          </div>
          <CButton color="secondary" variant="outline" size="sm" onClick={resetAll}>
            Replace File
          </CButton>
        </CCardBody>
      </CCard>

      <div className="d-flex gap-3 flex-wrap mb-3">
        <StatTile label="Valid" value={summary.valid} tone="success" icon={cilCheckCircle} />
        <StatTile
          label="Needs Attention"
          value={summary.needsAttention}
          tone="warning"
          icon={cilWarning}
        />
      </div>

      <CCard className="border-secondary-subtle rounded-4 overflow-hidden">
        <div className="table-responsive">
          <CTable align="middle" hover className="mb-0 pi-import-table">
            <CTableHead style={{ backgroundColor: 'var(--cui-tertiary-bg, #f8f9fa)' }}>
              <CTableRow>
                <CTableHeaderCell className="ps-4">Row</CTableHeaderCell>
                <CTableHeaderCell>Round</CTableHeaderCell>
                <CTableHeaderCell>Start Date</CTableHeaderCell>
                <CTableHeaderCell>Start Time</CTableHeaderCell>
                <CTableHeaderCell>End Date</CTableHeaderCell>
                <CTableHeaderCell>End Time</CTableHeaderCell>
                <CTableHeaderCell>Result</CTableHeaderCell>
                <CTableHeaderCell>Action</CTableHeaderCell>
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {rows.map((r) => {
                const meta = RESULT_META[r.result] || RESULT_META.valid
                const isEditing = editingRow === r.row
                return (
                  <CTableRow
                    key={r.row}
                    style={{
                      ...tintStyle(meta.accent, '0d'),
                      borderLeft: `3px solid ${meta.accent}`,
                    }}
                  >
                    <CTableDataCell className="ps-4">{r.row}</CTableDataCell>
                    <CTableDataCell>
                      {isEditing ? (
                        <CFormInput
                          type="number"
                          min="1"
                          size="sm"
                          value={editDraft.round}
                          onChange={(e) => setEditDraft((d) => ({ ...d, round: e.target.value }))}
                        />
                      ) : (
                        r.round || '—'
                      )}
                    </CTableDataCell>
                    <CTableDataCell>
                      {isEditing ? (
                        <CFormInput
                          type="date"
                          size="sm"
                          value={editDraft.start_date}
                          onChange={(e) =>
                            setEditDraft((d) => ({ ...d, start_date: e.target.value }))
                          }
                        />
                      ) : (
                        displayDate(r.start_date)
                      )}
                    </CTableDataCell>
                    <CTableDataCell>
                      {isEditing ? (
                        <CFormInput
                          type="time"
                          size="sm"
                          value={editDraft.start_time}
                          onChange={(e) =>
                            setEditDraft((d) => ({ ...d, start_time: e.target.value }))
                          }
                        />
                      ) : (
                        displayTime(r.start_time)
                      )}
                    </CTableDataCell>
                    <CTableDataCell>
                      {isEditing ? (
                        <CFormInput
                          type="date"
                          size="sm"
                          value={editDraft.end_date}
                          onChange={(e) =>
                            setEditDraft((d) => ({ ...d, end_date: e.target.value }))
                          }
                        />
                      ) : (
                        displayDate(r.end_date)
                      )}
                    </CTableDataCell>
                    <CTableDataCell>
                      {isEditing ? (
                        <CFormInput
                          type="time"
                          size="sm"
                          value={editDraft.end_time}
                          onChange={(e) =>
                            setEditDraft((d) => ({ ...d, end_time: e.target.value }))
                          }
                        />
                      ) : (
                        displayTime(r.end_time)
                      )}
                    </CTableDataCell>
                    <CTableDataCell>
                      <CBadge
                        color={meta.color}
                        shape="rounded-pill"
                        className="d-inline-flex align-items-center gap-1 px-2 py-1"
                      >
                        <CIcon icon={meta.icon} size="sm" />
                        {meta.label}
                      </CBadge>
                    </CTableDataCell>
                    <CTableDataCell>
                      {isEditing ? (
                        <div className="d-flex gap-1">
                          <CButton size="sm" color="primary" onClick={() => saveEdit(r.row)}>
                            <CIcon icon={cilCheck} />
                          </CButton>
                          <CButton
                            size="sm"
                            color="secondary"
                            variant="outline"
                            onClick={cancelEdit}
                          >
                            <CIcon icon={cilX} />
                          </CButton>
                        </div>
                      ) : (
                        <div className="d-flex gap-2">
                          <CButton
                            size="sm"
                            color="link"
                            className="p-0"
                            onClick={() => startEdit(r)}
                          >
                            Edit
                          </CButton>
                          {r.result === 'valid' && (
                            <CButton
                              size="sm"
                              color="link"
                              className="p-0"
                              onClick={() => toggleRowIncluded(r.row)}
                            >
                              {r.included ? 'Skip' : 'Include'}
                            </CButton>
                          )}
                        </div>
                      )}
                    </CTableDataCell>
                  </CTableRow>
                )
              })}
            </CTableBody>
          </CTable>
        </div>
      </CCard>

      <div className="mt-3">
        <InfoBox>
          Rows that need correction are excluded from the import until fixed. Use{' '}
          <strong>Edit</strong> to fix a row inline, or <strong>Skip</strong> to leave a valid row
          out of this import.
        </InfoBox>
      </div>

      <div className="d-flex justify-content-between mt-3">
        <CButton color="secondary" variant="outline" onClick={() => goToStep(1)}>
          <CIcon icon={cilArrowLeft} className="me-1" />
          Back to Upload
        </CButton>
        <CButton color="primary" disabled={!readyRows.length} onClick={() => goToStep(3)}>
          Continue to Review
        </CButton>
      </div>
    </>
  )

  const renderReview = () => {
    const excluded = rows.length - readyRows.length
    return (
      <>
        {importErrors.length > 0 && (
          <div className="mb-3">
            {importErrors.map((error, index) => (
              <CAlert color="danger" key={index}>
                {error}
              </CAlert>
            ))}
          </div>
        )}
        <CRow className="g-3 mb-3">
          <CCol sm={6} lg={3}>
            <StatTile label="File Rows" value={rows.length} tone="secondary" icon={cilFile} />
          </CCol>
          <CCol sm={6} lg={3}>
            <StatTile
              label="Ready to Import"
              value={readyRows.length}
              tone="success"
              icon={cilCheckCircle}
            />
          </CCol>
          <CCol sm={6} lg={3}>
            <StatTile label="Excluded" value={excluded} tone="warning" icon={cilWarning} />
          </CCol>
          <CCol sm={6} lg={3}>
            <StatTile
              label="Needs Correction"
              value={summary.needsAttention}
              tone="danger"
              icon={cilWarning}
            />
          </CCol>
        </CRow>
        <InfoBox>
          This will create {readyRows.length} new round record{readyRows.length === 1 ? '' : 's'}.
          If any row conflicts with an existing round, the whole import is rejected — review the
          list below before importing.
        </InfoBox>
        <CCard className="border-secondary-subtle rounded-4 overflow-hidden mt-3">
          <div className="table-responsive" style={{ maxHeight: 320, overflowY: 'auto' }}>
            <CTable align="middle" hover className="mb-0 pi-import-table">
              <CTableHead style={{ backgroundColor: 'var(--cui-tertiary-bg, #f8f9fa)' }}>
                <CTableRow>
                  <CTableHeaderCell className="ps-4">Round</CTableHeaderCell>
                  <CTableHeaderCell>Start Date</CTableHeaderCell>
                  <CTableHeaderCell>Start Time</CTableHeaderCell>
                  <CTableHeaderCell>End Date</CTableHeaderCell>
                  <CTableHeaderCell>End Time</CTableHeaderCell>
                </CTableRow>
              </CTableHead>
              <CTableBody>
                {readyRows.map((r) => (
                  <CTableRow key={r.row}>
                    <CTableDataCell className="ps-4">{r.round}</CTableDataCell>
                    <CTableDataCell>{displayDate(r.start_date)}</CTableDataCell>
                    <CTableDataCell>{displayTime(r.start_time)}</CTableDataCell>
                    <CTableDataCell>{displayDate(r.end_date)}</CTableDataCell>
                    <CTableDataCell>{displayTime(r.end_time)}</CTableDataCell>
                  </CTableRow>
                ))}
              </CTableBody>
            </CTable>
          </div>
        </CCard>
        <div className="d-flex justify-content-between mt-3">
          <CButton color="secondary" variant="outline" onClick={() => goToStep(2)}>
            <CIcon icon={cilArrowLeft} className="me-1" />
            Back
          </CButton>
          <CLoadingButton
            color="success"
            loading={importing}
            disabled={!readyRows.length}
            onClick={runImport}
          >
            <CIcon icon={cilCloudUpload} className="me-1" />
            Import {readyRows.length} Round{readyRows.length === 1 ? '' : 's'}
          </CLoadingButton>
        </div>
      </>
    )
  }

  return (
    <>
      <style>{`
        .pi-import-table > :not(caption) > * > * { padding-top: .85rem; padding-bottom: .85rem; }
      `}</style>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h4 className="fw-bold mb-0">Import Rounds</h4>
          <div className="small text-medium-emphasis">
            Create multiple round records from a spreadsheet.
          </div>
        </div>
        <CButton color="secondary" variant="outline" onClick={() => navigate('/rounds')}>
          <CIcon icon={cilArrowLeft} className="me-1" />
          Back to Rounds
        </CButton>
      </div>
      <CCard className="mb-4 border-0 shadow-sm">
        <CCardBody>
          <MiniStepper current={wizardStep} maxReached={maxWizardStep} onStepClick={goToStep} />
          {wizardStep === 1 && renderUpload()}
          {wizardStep === 2 && renderValidate()}
          {wizardStep === 3 && renderReview()}
        </CCardBody>
      </CCard>
    </>
  )
}

export default ImportRound
